import { Error, createError, ErrorCodes } from '@quickplay/foundation';
import { PlayerErrorCodes } from '../../PlayerErrorCodes';

// Todo: Need to decide whether to expose Player Error or not .(If it is needed in the play-auth-player),
const createPlayerError = (
  errorCode: number,
  errorMessage?: string,
  contextDescription?: string,
  internalError?: Error,
): Error => {
  return createError(
    errorCode | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
    errorMessage || 'Unknown player error occurred.',
    internalError,
    contextDescription,
  );
};

const shakaErrorToError = (shakaError: any): Error => {
  return createError(shakaError.code, JSON.stringify(shakaError));
};

// Refer: shaka.util.Error.Code.RESTRICTIONS_CANNOT_BE_MET (4012).
// Shaka surfaces HDCP/output-protection failures via the `restrictedKeyStatuses`
// array on the error's `data`, reporting `'output-restricted'` when the connected
// output (e.g. non-HDCP-compliant display, mirroring or capture device) doesn't
// meet the DRM license's required HDCP compliance level.
const isHdcpRestrictionError = (shakaError: any): boolean => {
  return (
    Array.isArray(shakaError.data) &&
    shakaError.data.some(
      (entry: any) =>
        entry &&
        Array.isArray(entry.restrictedKeyStatuses) &&
        entry.restrictedKeyStatuses.includes('output-restricted'),
    )
  );
};

const getPlayerErrorBasedOnShakaErrorCategory = (shakaError: any, shakaErrorUtilErrorCategory: any): Error => {
  const shakaContextMessage = `${shakaError.code}  ${shakaError.message}`;
  const internalError = shakaErrorToError(shakaError);

  switch (shakaError.category) {
    //NETWORK	:	Errors from the network stack.
    case shakaErrorUtilErrorCategory.NETWORK:
      // Except shaka error code 1002 and 1003 all the other errors are server errors
      return createPlayerError(
        PlayerErrorCodes.MEDIA_SOURCE_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_SERVER,
        'Unexpected Network error.',
        shakaContextMessage,
        internalError,
      );
    case shakaErrorUtilErrorCategory.MEDIA:
      // Refer: [CBT-6196]
      // In Xbox, If an user siwtches between the HDMI sources during the playback or switching
      // between the output devices which are incompatible to render the chunks will be errored out with MS hex error code.
      // This needs to be addressed as a separate error code because shaka player doesn't have any control over it.
      if (shakaError.data && shakaError.data.length >= 1 && shakaError.data[1]) {
        return createPlayerError(
          PlayerErrorCodes.SYSTEM_FAILURE,
          `Unsupported device action or incompatible output devices are used.`,
          shakaContextMessage,
          internalError,
        );
      }
      return createPlayerError(
        PlayerErrorCodes.MEDIA_PLAYBACK_FAILURE,
        `Errors parsing or processing audio or video streams.`,
        shakaContextMessage,
        internalError,
      );

    // MANIFEST:	Errors parsing the Manifest.
    case shakaErrorUtilErrorCategory.MANIFEST:
    // STREAMING:	Errors related to streaming.
    case shakaErrorUtilErrorCategory.STREAMING:
      return createPlayerError(
        PlayerErrorCodes.MEDIA_SOURCE_FAILURE,
        `Errors while parsing the Manifest | streams.`,
        shakaContextMessage,
        internalError,
      );
    case shakaErrorUtilErrorCategory.DRM:
      return createPlayerError(
        PlayerErrorCodes.DRM_LICENSE_FAILURE,
        `Error due to DRM licence failure.`,
        shakaContextMessage,
        internalError,
      );
    default:
      return createPlayerError(
        PlayerErrorCodes.MEDIA_PLAYBACK_FAILURE,
        `Media Playback has failed.`,
        shakaContextMessage,
        internalError,
      );
  }
};

export const convertToPlayerError = (shakaErrorEvent: any, shakaErrorUtil: any): Error => {
  const shakaError = shakaErrorEvent.detail;
  const shakaContextMessage = `${shakaError.code}  ${shakaError.message}`;
  const shakaErrorUtilCode = shakaErrorUtil.Code;
  const internalError = shakaErrorToError(shakaError);

  // HDCP/output-protection restriction failures (e.g. RESTRICTIONS_CANNOT_BE_MET) can surface
  // under either the MANIFEST or DRM shaka category, so they're checked ahead of the
  // code/category based routing below to always map to a dedicated HDCP error code.
  if (isHdcpRestrictionError(shakaError)) {
    return createPlayerError(
      PlayerErrorCodes.HDCP_COMPLIANCE_FAILURE,
      `Output device does not meet the required HDCP compliance level.`,
      shakaContextMessage,
      internalError,
    );
  }

  switch (shakaError.code) {
    case shakaErrorUtilCode.HTTP_ERROR:
      return createPlayerError(
        PlayerErrorCodes.MEDIA_SOURCE_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_NETWORK,
        'HTTP network request failed with an error, but not from the server.',
        shakaContextMessage,
        internalError,
      );
    case shakaErrorUtilCode.TIMEOUT:
      return createPlayerError(
        PlayerErrorCodes.MEDIA_SOURCE_TIMEOUT | ErrorCodes.ERROR_CATEGORY_MASK_NETWORK,
        'A network request timed out.',
        shakaContextMessage,
        internalError,
      );
    case shakaErrorUtilCode.INVALID_SERVER_CERTIFICATE:
    case shakaErrorUtilCode.SERVER_CERTIFICATE_REQUIRED:
      return createPlayerError(
        PlayerErrorCodes.DRM_PROVISIONING_FAILURE,
        `Drm Provisioning wasn't available when it is required.`,
        shakaContextMessage,
        internalError,
      );
    case shakaErrorUtilCode.EXPIRED:
      return createPlayerError(
        PlayerErrorCodes.DRM_LICENSE_EXPIRED,
        `Drm license has expired.`,
        shakaContextMessage,
        internalError,
      );
    case shakaErrorUtilCode.LOAD_INTERRUPTED:
      // The call to Player.load() was interrupted by a call to Player.unload() or another call to Player.load().
      return createPlayerError(
        PlayerErrorCodes.MEDIA_PLAYBACK_FAILURE,
        `Too-many instances of Player has been created Simultaneously.`,
        shakaContextMessage,
        internalError,
      );
    case shakaErrorUtilCode.NO_RECOGNIZED_KEY_SYSTEMS:
    case shakaErrorUtilCode.REQUESTED_KEY_SYSTEM_CONFIG_UNAVAILABLE:
    case shakaErrorUtilCode.CONTENT_UNSUPPORTED_BY_BROWSER:
    case shakaErrorUtilCode.DASH_UNSUPPORTED_XLINK_ACTUATE:
    case shakaErrorUtilCode.HLS_COULD_NOT_GUESS_CODECS:
    case shakaErrorUtilCode.UNSUPPORTED_SCHEME:
      return createPlayerError(
        PlayerErrorCodes.CONTENT_UNSUPPORTED_BY_BROWSER,
        `Browser has failed to handle the Unsupported Media Content or KeySystem.`,
        shakaContextMessage,
        internalError,
      );
    default:
      return getPlayerErrorBasedOnShakaErrorCategory(shakaError, shakaErrorUtil.Category);
  }
};
